import axios from "../../Axios";
import { Urls } from "../../Urls";
import { useQuery, useMutation } from "@tanstack/react-query";

/**
 * Fetches paginated spot enquiries from the server.
 * Extracted from NewEnquiry.fetchSpotData().
 *
 * @param {Object} payload - The filter/pagination payload sent to the API.
 * @returns {Promise<{
 *   dataList: Array,
 *   totalCount: number,
 *   limit: number|null,
 *   isApprover: boolean
 * }>}
 */
export const fetchSpotEnquiries = async (payload) => {
  const res = await axios.post(Urls.spot_data, payload);

  const totalCount =
    res.data.total_count ||
    res.data.total_enquiries ||
    res.data.total ||
    res.data.all_documents_count ||
    0;

  const dataList =
    res.data.enquiry_list ||
    res.data.data ||
    (res.data.filtered_data && res.data.filtered_data.enquiry_list) ||
    [];

  const limit =
    res.data.limit && [10, 20, 50].includes(res.data.limit)
      ? res.data.limit
      : null;

  const isApprover = res.data.is_approver === true;

  return { dataList, totalCount, limit, isApprover, rawData: res.data };
};

export const useSpotEnquiriesMutation = () => {
  return useMutation({
    mutationFn: fetchSpotEnquiries,
  });
};

/**
 * Fetches unread chat message counts for a set of enquiry IDs.
 * Extracted from NewEnquiry.reload_data().
 *
 * @param {Array<string>} shardIds - List of truck_enquiry_id values.
 * @param {string} companyMail - The logged-in user's email address.
 * @returns {Promise<Array>} Array of message count objects from the chat API.
 */
export const fetchChatCounts = async ({ shardIds, companyMail }) => {
  const res = await axios.post(
    `${Urls.enquiryChat}&email=${companyMail}&id_specific=true`,
    { shard_ids: shardIds },
  );
  return res.data;
};

export const useChatCountsMutation = () => {
  return useMutation({
    mutationFn: fetchChatCounts,
  });
};

/**
 * Fetches all truck types grouped into "My trucks" and "Other Trucks".
 * Extracted from NewEnquiry.getTruckTypes().
 *
 * @returns {Promise<Array>} Grouped truck type option array for react-select.
 */
export const fetchTruckTypeOptions = async () => {
  const res = await axios.get(
    `${Urls.Autocomplete.Get.getlist}?searchFields=truck&include_company_trucks=true`,
  );

  const myTrucks = [];
  const otherTrucks = [];

  res.data.forEach((item) => {
    if (item.name) {
      const option = {
        label: item.name,
        value: item.name,
        truck_types_group: item.network_truck_type_group,
        isMyTruck: item.from_truck_master ? true : false,
      };
      if (item.from_truck_master) {
        myTrucks.push(option);
      } else {
        otherTrucks.push(option);
      }
    }
  });

  return [
    { label: "My trucks", options: myTrucks },
    { label: "Other Trucks", options: otherTrucks },
  ];
};

export const useTruckTypeOptionsQuery = () => {
  return useQuery({
    queryKey: ["truckTypeOptions"],
    queryFn: fetchTruckTypeOptions,
    staleTime: 5 * 60 * 1000,
  });
};

export const fetchPlanningUsers = async (email) => {
  const res = await axios.get(`${Urls.user_data}?field=all_users&email=${email}`);
  return res.data;
};

export const usePlanningUsersQuery = (email, options) => {
  return useQuery({
    queryKey: ["planningUsers", email],
    queryFn: () => fetchPlanningUsers(email),
    staleTime: 5 * 60 * 1000,
    ...options
  });
};

